/**
 * useActivityTracking
 * ===================
 *
 * Mounts a ContentActivity row on the backend when a lesson sub-tab opens,
 * and closes it on unmount. Powers all time-based progress metrics -
 * study hours, activity heatmap, per-content-type breakdown, per-lesson
 * Coverage, and the streak (via meaningful-session aggregation).
 *
 * Behavior:
 * - On mount: POST /activity/start, stores the activity_id.
 * - On unmount: POST /activity/{id}/end.
 * - `beforeunload` and `visibilitychange` (when the tab is hidden) trigger
 *   a synchronous end so a tab close mid-session doesn't strand an open
 *   ContentActivity row.
 * - When the tab becomes visible again, a NEW activity starts. It used to
 *   stay ended, so one trip to another app stopped the sub-tab counting
 *   until it was remounted - the rest of that session's study time was
 *   never recorded (caught by `Lesson/Viewer/TracksTimeOnEachTab`).
 * - If the user is unauthenticated or the start call fails, the hook is a
 *   no-op - sub-tabs render normally; analytics just doesn't capture this
 *   instance. We swallow errors rather than disrupting the lesson UX.
 *
 * Usage:
 *   useActivityTracking({ contentType: 'text', lessonId });
 *
 * Pass `enabled: false` to skip tracking (e.g. during prefetch or when the
 * tab is rendered but not yet visible).
 */

import { useEffect, useRef } from 'react';
import { track } from '@/config/observability';
import { activityApi, type ActivityContentType } from '@/lib/api-client';

interface UseActivityTrackingOptions {
  contentType: ActivityContentType;
  lessonId?: string;
  conceptId?: string;
  enabled?: boolean;
}

export function useActivityTracking({
  contentType,
  lessonId,
  conceptId,
  enabled = true,
}: UseActivityTrackingOptions): void {
  // Ref so cleanup callbacks see the latest activity_id without re-subscribing.
  const activityIdRef = useRef<string | null>(null);
  // Latch so we never call end twice (e.g. beforeunload then unmount).
  const endedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!enabled || !contentType) {
      return;
    }

    let cancelled = false;
    endedRef.current = false;

    const endIfOpen = () => {
      const id = activityIdRef.current;
      if (!id || endedRef.current) return;
      endedRef.current = true;
      // Fire-and-forget - we can't await unload handlers. Backend processes
      // the close best-effort. Use `keepalive` so the request survives
      // tab teardown.
      try {
        activityApi.endActivity(id).catch(() => {
          // Swallow - see header comment.
        });
      } catch {
        // ignore
      }
    };

    const start = async () => {
      try {
        const res = await activityApi.startActivity(
          contentType,
          lessonId,
          conceptId
        );
        if (cancelled) {
          // Hook tore down before start resolved - close immediately.
          activityApi.endActivity(res.activity_id).catch(() => {});
          return;
        }
        activityIdRef.current = res.activity_id;
        endedRef.current = false;
        // Hidden while the start was in flight: `endIfOpen` had no id to close
        // then, so close it now rather than count time nobody is studying.
        if (document.visibilityState === 'hidden') endIfOpen();
      } catch {
        // Unauthenticated or backend down - silently degrade.
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        endIfOpen();
      } else if (endedRef.current) {
        // Back from another tab or app: this is a new stretch of study time.
        // Cleared first so a second `visible` before the start resolves does
        // not open two rows.
        endedRef.current = false;
        activityIdRef.current = null;
        void start();
      }
    };

    // Once per mount, not per visibility restart: the product event is "this
    // tab was opened", the backend row above is the time spent.
    if (lessonId) {
      track('lesson_tab_viewed', {
        lesson_id: lessonId,
        content_type: contentType,
      });
    }
    void start();

    window.addEventListener('beforeunload', endIfOpen);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled = true;
      window.removeEventListener('beforeunload', endIfOpen);
      document.removeEventListener('visibilitychange', handleVisibility);
      endIfOpen();
      activityIdRef.current = null;
    };
    // contentType / lessonId / conceptId changes restart tracking (the
    // previous activity ends and a new one begins). `enabled` toggling
    // mid-flight starts/stops appropriately.
  }, [contentType, lessonId, conceptId, enabled]);
}
